import React, { useEffect, useMemo, useState } from "react";
import { Box, ThemeProvider } from "@mui/material";
import theme from "theme";
import bg1 from "assets/bg_survey_wealth_01.svg";
import logo from "assets/logo.svg";
import wealthIcon from "assets/wealth/icons/wealth-icon.svg";
import { ResponsivePie } from "@nivo/pie";
import useDevice from "hooks/useDevice";
import { useLocation, useParams } from "react-router-dom";
import { getWealthResult } from "services";
import { useSnackbar } from "notistack";
import { colorArray } from "constants";
import Loading from "components/Loading";

const PieChartComponent = ({ dataChart }) => (
  <div style={{ height: "294px" }}>
    <ResponsivePie
      data={dataChart}
      margin={{ top: 4, right: 16, bottom: 4, left: 16 }}
      innerRadius={0}
      padAngle={0.7}
      cornerRadius={3}
      activeOuterRadiusOffset={8}
      colors={{ datum: "data.color" }}
      borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
      arcLinkLabelsSkipAngle={360}
      arcLabelsSkipAngle={10}
      arcLabelsTextColor="#FFF"
      arcLabel={(e) => `${e.value}%`}
      theme={{
        labels: {
          text: {
            fontWeight: 700,
            fontSize: 16,
            lineHeight: 1.2,
            textAlign: "center",
          },
        },
      }}
      tooltip={({ datum }) => (
        <div
          style={{
            width: "205px",
            padding: "16px",
            borderRadius: "8px",
            background: "#FFFFFF",
            boxShadow: "0px 4px 16px 0px #00000029",
          }}
        >
          <strong
            style={{
              display: "block",
              color: datum.color,
              fontWeight: 600,
              fontSize: "24px",
              lineHeight: 1.2,
              letterSpacing: 0,
              marginBottom: "12px",
            }}
          >
            {datum.value}%
          </strong>
          <div style={{ fontWeight: 600, fontSize: "14px", color: "#25282B" }}>
            {datum.label}
          </div>
        </div>
      )}
    />
  </div>
);

const SurveyWealthResult = () => {
  const { isPC } = useDevice();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { surveyId, id } = useParams();
  const [dataSurvey, setDataSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryParams = new URLSearchParams(location.search);

  const isSuccess = queryParams.get("isSuccess");
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!id) return;

      setLoading(true);
      const maxRetries = 3;
      const retryDelay = 1500;
      let attempt = 0;
      let success = false;

      while (attempt < maxRetries && isMounted && !success) {
        console.log(`📡 Attempt ${attempt + 1}`);
        try {
          let isMailing = "true";
          if (isSuccess) {
            isMailing = "false";
          }
          const res = await getWealthResult(id, isMailing);

          if (res && Object.keys(res).length > 0) {
            setDataSurvey(res);
            success = true;
            break;
          }
          if (++attempt < maxRetries) {
            await new Promise((r) => setTimeout(r, retryDelay));
          }
        } catch (e) {
          const msg = e?.response?.data?.message ?? "";
          const isNoDataMsg =
            msg.includes("Khảo sát không tồn tại") ||
            msg.includes("không có kết quả");

          // Nếu là lỗi có thể retry
          if (e?.response?.status === 400 && isNoDataMsg) {
            if (++attempt < maxRetries) {
              await new Promise((r) => setTimeout(r, retryDelay));
              continue;
            }
            break; // hết retry
          }

          enqueueSnackbar(msg || "Có lỗi xảy ra khi tải dữ liệu", {
            variant: "error",
            autoHideDuration: 3000,
          });
          break;
        }
      }

      if (!success && isMounted) {
        enqueueSnackbar("Khảo sát không tồn tại hoặc không có kết quả", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }

      if (isMounted) setLoading(false);
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [surveyId, id, enqueueSnackbar, isSuccess]);

  const dataChart = useMemo(() => {
    if (dataSurvey?.assetMap) {
      const keys = Object.keys(dataSurvey?.assetMap);
      return keys?.map((key, index) => ({
        id: key,
        value: dataSurvey.assetMap[key],
        color: colorArray[index],
        label: key,
      }));
    }
    return [];
  }, [dataSurvey]);

  const handleSuccess = () => {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has("isSuccess")) {
      window.location.href = currentUrl.href + "?isSuccess=true";
    } else {
      window.location.href = currentUrl.href;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ background: "#FFFFFF", height: "100vh", overflowX: "hidden" }}>
        <Box
          className={
            "p-4 !pb-[41px] md:p-6 relative max-w-[650px] mx-auto" +
            `${!isPC ? " mb-[102px]" : ""}`
          }
        >
          <img
            src={bg1}
            alt="decor"
            className="absolute top-[44px] right-0 z-0"
          />
          <img src={logo} alt="VPBank" className="w-[154px] mx-auto" />
          <div className="py-4 font-bold text-[1.125rem] text-center uppercase text-[#00B74F]">
            Kết quả Hồ sơ Khảo sát Khẩu vị rủi ro của Khách hàng
          </div>
          <div
            className="font-medium text-[16px] leading-normal tracking-[0] text-center"
            dangerouslySetInnerHTML={{
              __html: `Căn cứ theo khả năng chấp nhận rủi ro của Quý Khách hàng tại thời điểm hiện tại và thời hạn định hướng sử dụng tài sản theo các câu trả lời trong Bảng câu hỏi khảo sát, chúng tôi đề xuất mức độ chấp nhận rủi ro tổng thể của Quý khách hàng là:`,
            }}
          />
          <div className="mt-4 w-full rounded-lg p-4 bg-[#F1F3F9]">
            <div className="rounded-lg p-8 flex items-center justify-center bg-[#FFF]">
              {loading ? (
                <Loading loading={loading} />
              ) : (
                <span className="font-bold text-[18px] md:text-[32px] tracking-[0] text-center uppercase text-[#68ACDD]">
                  {dataSurvey?.profileName}
                </span>
              )}
            </div>
            <div className="mt-4 text-center text-[#25282B] font-medium text-[14px] leading-normal tracking-[0]">
              Thang mức độ chấp nhận rủi ro theo 5 cấp độ (1: Rất thận trọng, 2:
              Thận trọng, 3: Cân bằng, 4: Mạo hiểm, 5: Rất mạo hiểm)
            </div>
          </div>
          <div className="mt-4 w-full rounded-lg p-4 bg-[#F1F3F9]">
            <div className="font-bold text-[18px] leading-normal tracking-[0] text-center uppercase">
              Gợi ý phân bổ danh mục loại tài sản
            </div>
            {loading ? (
              <div className="flex items-center justify-center min-h-[350px]">
                <Loading loading={loading} />
              </div>
            ) : (
              <div className="flex md:flex-row flex-col justify-center md:justify-between items-center">
                <div className="w-full md:w-[50%] my-8 md:my-0">
                  <PieChartComponent dataChart={dataChart} />
                </div>
                <div className="md:w-[50%] md:pl-8">
                  {dataChart?.map((d, idx) => {
                    return (
                      <div
                        className="flex items-center mb-4"
                        key={`legend-${idx}`}
                      >
                        <span
                          className="mr-2 inline-block w-[14px] h-[14px] rounded-full"
                          style={{ background: d.color }}
                        ></span>
                        <span className="text-[#25282B] font-medium text-[14px] leading-[1.2] tracking-[0]">
                          {d.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 w-full rounded-lg p-4 bg-[#FFF7E0] flex-col-reverse md:flex-row flex items-center justify-between gap-4">
            <div className="font-semibold text-[16px] leading-normal tracking-[0] text-center md:w-[62%]">
              Vui lòng xem chi tiết các thông tin và đề xuất tại{" "}
              <span className="text-[#00B74F]">
                Báo cáo phân tích và đề xuất giải pháp
              </span>{" "}
              đã được gửi tới Email của Quý khách hàng.
            </div>
            <div className="">
              <img className="w-full" src={wealthIcon} alt="" />
            </div>
          </div>
          <div className="flex flex-col items-center justify-center py-[26px]">
            <img src={logo} alt="VPBank" className="w-[120px] mb-4" />
            <div className="text-[#0B263D] font-semibold text-[16px] leading-[1.4] tracking-[0] text-center mb-2">
              Ngân hàng TMCP Việt Nam Thịnh Vượng
            </div>
            <div className="text-[#526779] font-medium text-[14px] leading-[1.4] tracking-[0] text-center mb-2">
              Trụ sở chính: 89 Láng Hạ, Quận Đống Đa, Hà Nội
            </div>
            <div className="text-[#526779] font-bold text-[14px] leading-[18px] tracking-[0] text-center mb-2">
              Hỗ trợ{" "}
              <span className="text-[#00AD53]">
                (84) 24 392 88880 / 1900545415
              </span>
            </div>
          </div>
          <div className="absolute w-full bg-gradient py-3 px-6 bottom-0 left-0 flex md:flex-row flex-col md:justify-between items-center text-[#FFF]">
            <div className="font-medium text-[12px] leading-[1.4] tracking-[0]">
              © 2020 VPBank – Bản quyền đã được bảo hộ
            </div>
            <div className="font-medium text-[12px] leading-[1.4] tracking-[0]">
              www.vpbank.com.vn
            </div>
          </div>
          {!isPC && (
            <div className="fixed bottom-0 left-0 bg-white w-full px-4 py-3 pb-[46px] [box-shadow:0_0_8px_0_rgba(0,_0,_0,_0.10)]">
              <button
                className="w-full py-3 flex items-center justify-center rounded-xl bg-[#01AD53] text-white text-[16px] font-medium leading-tight"
                onClick={handleSuccess}
              >
                Hoàn tất
              </button>
            </div>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default SurveyWealthResult;
